// Federal compliance obligation catalog viewer (read-only).
// Shows the 84 federal regulatory obligations the compliance engine
// tracks against, grouped by category_label.
import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';

type Obligation = {
  id: string;
  code: string;
  name: string;
  category: string;
  category_label: string;
  source_cite: string | null;
  cadence_rule: string | null;
  applies_when: any;
  evidence_type: string | null;
  owner_role: string | null;
  auto_level: string | null;
  is_active: boolean;
  display_order: number;
  applies_to_grants: string[] | null;
  shared_satisfies_once: boolean;
  review_status: string | null;
  review_note: string | null;
};

const CADENCE_LABEL: Record<string, string> = {
  one_time: 'One time',
  annual: 'Annual',
  quarterly: 'Quarterly',
  monthly: 'Monthly',
  weekly: 'Weekly',
  per_draw: 'Per draw',
  per_milestone: 'Per milestone',
  per_shipment: 'Per shipment',
  continuous: 'Continuous',
  pre_construction: 'Pre-construction',
  semi_annual: 'Semi-annual',
};

const AUTO_LEVEL_COLOR: Record<string, { bg: string; fg: string }> = {
  full_auto:   { bg: '#dcfce7', fg: '#047857' },
  semi_auto:   { bg: '#fef3c7', fg: '#92400e' },
  manual:      { bg: '#f1f5f9', fg: '#475569' },
};

export default function ObligationCatalogPage() {
  const { data: obligations = [], isLoading } = useQuery<Obligation[]>({
    queryKey: ['obligation-catalog'],
    queryFn: () => api.get('/bead/obligation-catalog'),
  });

  const qc = useQueryClient();
  const reviewMut = useMutation({
    mutationFn: (v: { id: string; action: string }) => api.put(`/bead/obligations/${v.id}/review`, { action: v.action }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['obligation-catalog'] }),
  });
  const [search, setSearch] = useState('');
  const [filterCategory, setFilterCategory] = useState<string | 'all'>('all');
  const [filterGrant, setFilterGrant] = useState<string | 'all'>('all');

  const groups = useMemo(() => {
    const filtered = obligations.filter((o) => {
      if (filterGrant !== 'all' && !(o.applies_to_grants ?? ['BEAD']).includes(filterGrant)) return false;
      if (filterCategory !== 'all' && o.category_label !== filterCategory) return false;
      if (search.trim()) {
        const q = search.toLowerCase();
        return (
          o.code.toLowerCase().includes(q) ||
          o.name.toLowerCase().includes(q) ||
          (o.source_cite ?? '').toLowerCase().includes(q)
        );
      }
      return true;
    });
    const m = new Map<string, Obligation[]>();
    for (const o of filtered) {
      if (!m.has(o.category_label)) m.set(o.category_label, []);
      m.get(o.category_label)!.push(o);
    }
    return Array.from(m.entries());
  }, [obligations, search, filterCategory, filterGrant]);

  const allCategories = useMemo(() => {
    const s = new Set<string>();
    obligations.forEach((o) => s.add(o.category_label));
    return Array.from(s).sort();
  }, [obligations]);

  const allGrants = useMemo(() => {
    const s = new Set<string>();
    obligations.forEach((o) => (o.applies_to_grants ?? ['BEAD']).forEach((g) => s.add(g)));
    return Array.from(s).sort();
  }, [obligations]);

  if (isLoading) return <div className="card"><div className="card__body">Loading…</div></div>;

  return (
    <div style={{ maxWidth: 1200 }}>
      <div className="page__header">
        <div>
          <h1 className="page__title">Compliance — Obligation Catalog</h1>
          <div className="page__subtitle">
            Federal regulatory obligations the compliance engine tracks against. {obligations.length} total · grouped by category.
          </div>
        </div>
      </div>
      {(() => { const pend = obligations.filter((o) => o.review_status === 'agent_draft').length; return pend > 0 ? (
        <div style={{ background:'#fffbeb', border:'1px solid #fcd34d', color:'#92400e', borderRadius:6, padding:'8px 12px', marginBottom:12, fontSize:13 }}>
          <b>{pend}</b> AI-drafted obligation{pend===1?'':'s'} pending review — hover the AI DRAFT badge for the reason, then approve or reject.
        </div>
      ) : null; })()}

      {/* Search + filter */}
      <div style={{
        background: '#fff', border: '1px solid #e2e8f0', borderRadius: 6,
        padding: '10px 14px', marginBottom: 16,
        display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap',
      }}>
        <input
          type="search"
          placeholder="Search code / name / citation…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ flex: '1 1 280px', maxWidth: 400, padding: '5px 10px', fontSize: 13, border: '1px solid #cbd5e1', borderRadius: 4 }}
        />
        <select value={filterCategory} onChange={(e) => setFilterCategory(e.target.value as any)}
          style={{ padding: '5px 10px', fontSize: 13, border: '1px solid #cbd5e1', borderRadius: 4 }}>
          <option value="all">All categories ({obligations.length})</option>
          {allCategories.map((c) => (
            <option key={c} value={c}>{c} ({obligations.filter((o) => o.category_label === c).length})</option>
          ))}
        </select>
        <select value={filterGrant} onChange={(e) => setFilterGrant(e.target.value as any)}
          style={{ padding: '5px 10px', fontSize: 13, border: '1px solid #cbd5e1', borderRadius: 4 }}>
          <option value="all">All grants</option>
          {allGrants.map((g) => (
            <option key={g} value={g}>{g} ({obligations.filter((o) => (o.applies_to_grants ?? ['BEAD']).includes(g)).length})</option>
          ))}
        </select>
        <span style={{ fontSize: 11, color: '#64748b', marginLeft: 'auto' }}>
          Showing {groups.reduce((a, [, l]) => a + l.length, 0)} of {obligations.length}
        </span>
      </div>

      {/* Groups */}
      {groups.map(([label, items]) => (
        <div key={label} style={{
          background: '#fff', border: '1px solid #e2e8f0', borderRadius: 6,
          marginBottom: 14, overflow: 'hidden',
        }}>
          <div style={{
            background: '#f8fafc', padding: '8px 14px', borderBottom: '1px solid #e2e8f0',
            fontWeight: 700, fontSize: 13,
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          }}>
            <span>{label}</span>
            <span style={{ fontSize: 11, fontWeight: 400, color: '#64748b' }}>{items.length} obligation{items.length === 1 ? '' : 's'}</span>
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr style={{ background: '#fff' }}>
                <th style={{ textAlign: 'left', padding: '6px 10px', fontWeight: 600, color: '#475569', width: 160 }}>Code</th>
                <th style={{ textAlign: 'left', padding: '6px 10px', fontWeight: 600, color: '#475569' }}>Name</th>
                <th style={{ textAlign: 'left', padding: '6px 10px', fontWeight: 600, color: '#475569', width: 130 }}>Cadence</th>
                <th style={{ textAlign: 'left', padding: '6px 10px', fontWeight: 600, color: '#475569', width: 110 }}>Owner</th>
                <th style={{ textAlign: 'left', padding: '6px 10px', fontWeight: 600, color: '#475569', width: 110 }}>Automation</th>
              </tr>
            </thead>
            <tbody>
              {items.map((o) => {
                const al = AUTO_LEVEL_COLOR[o.auto_level ?? 'manual'] ?? AUTO_LEVEL_COLOR.manual;
                return (
                  <tr key={o.id} style={{ borderTop: '1px solid #f1f5f9' }}>
                    <td style={{ padding: '6px 10px', fontFamily: 'ui-monospace, monospace', fontSize: 11, fontWeight: 600, color: '#0f172a', verticalAlign: 'top' }}>
                      {o.code}
                    </td>
                    <td style={{ padding: '6px 10px', verticalAlign: 'top' }}>
                      <div>{o.name}</div>
                      {o.source_cite && (
                        <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 2, fontFamily: 'ui-monospace, monospace' }}>
                          {o.source_cite}
                        </div>
                      )}
                      <div style={{ marginTop: 3, display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                        {(o.applies_to_grants ?? ['BEAD']).map((g) => (
                          <span key={g} style={{ fontSize: 9, fontWeight: 600, background: '#eef2ff', color: '#4338ca', padding: '1px 5px', borderRadius: 3 }}>{g}</span>
                        ))}
                        {o.shared_satisfies_once && (
                          <span style={{ fontSize: 9, fontWeight: 600, background: '#ecfeff', color: '#0e7490', padding: '1px 5px', borderRadius: 3 }}>shared - do once</span>
                        )}
                        {o.review_status === 'agent_draft' && (
                          <span title={o.review_note || 'Agent-drafted - pending human review'} style={{ fontSize: 9, fontWeight: 700, background: '#fef9c3', color: '#854d0e', padding: '1px 5px', borderRadius: 3, cursor: 'help' }}>AI DRAFT - review</span>
                        )}
                        {o.review_status === 'agent_draft' && (
                          <span style={{ display:'inline-flex', gap:4, marginLeft:4 }}>
                            <button onClick={() => reviewMut.mutate({ id:o.id, action:'approve' })} title="Approve - mark verified" style={{ fontSize:9, fontWeight:700, background:'#dcfce7', color:'#047857', border:'1px solid #6ee7b7', borderRadius:3, padding:'1px 5px', cursor:'pointer' }}>\u2713 approve</button>
                            <button onClick={() => reviewMut.mutate({ id:o.id, action:'reject' })} title="Reject - remove from catalog" style={{ fontSize:9, fontWeight:700, background:'#fee2e2', color:'#b91c1c', border:'1px solid #fca5a5', borderRadius:3, padding:'1px 5px', cursor:'pointer' }}>\u2717 reject</button>
                          </span>
                        )}
                      </div>
                    </td>
                    <td style={{ padding: '6px 10px', verticalAlign: 'top', color: '#475569' }}>
                      {CADENCE_LABEL[o.cadence_rule ?? ''] ?? (o.cadence_rule ?? '—')}
                    </td>
                    <td style={{ padding: '6px 10px', verticalAlign: 'top', color: '#475569', textTransform: 'capitalize' }}>
                      {(o.owner_role ?? '—').replace(/_/g, ' ')}
                    </td>
                    <td style={{ padding: '6px 10px', verticalAlign: 'top' }}>
                      <span style={{
                        background: al.bg, color: al.fg,
                        padding: '2px 8px', borderRadius: 4,
                        fontSize: 10, fontWeight: 600,
                        textTransform: 'uppercase', letterSpacing: '.04em',
                      }}>
                        {(o.auto_level ?? 'manual').replace(/_/g, ' ')}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ))}

      {groups.length === 0 && (
        <div className="card"><div className="card__body" style={{ color: '#94a3b8' }}>
          No obligations match the current filter.
        </div></div>
      )}

      <div style={{ fontSize: 10, color: '#94a3b8', textAlign: 'right', marginTop: 8 }}>
        Catalog is read-only. Obligations are federal regulatory definitions; instances are tracked per-project under Compliance &rarr; Overview.
      </div>
    </div>
  );
}
